// ============================================================
// LinuxPermissions.jsx
//
// Branded, responsive course content page for the
// "Linux Permissions & Ownership" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box, Container, Typography, Grid, Chip, Divider,
    Tooltip, Collapse, Paper,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import LockIcon                from "@mui/icons-material/Lock";
import LockOpenIcon            from "@mui/icons-material/LockOpen";
import VisibilityIcon          from "@mui/icons-material/Visibility";
import EditIcon                from "@mui/icons-material/Edit";
import PlayArrowIcon           from "@mui/icons-material/PlayArrow";
import PersonIcon              from "@mui/icons-material/Person";
import GroupIcon               from "@mui/icons-material/Group";
import PublicIcon              from "@mui/icons-material/Public";
import LayersIcon              from "@mui/icons-material/Layers";
import SearchIcon              from "@mui/icons-material/Search";
import BuildIcon               from "@mui/icons-material/Build";
import SecurityIcon            from "@mui/icons-material/Security";
import TerminalIcon            from "@mui/icons-material/Terminal";
import SettingsIcon            from "@mui/icons-material/Settings";
import StorageIcon             from "@mui/icons-material/Storage";
import ArticleIcon             from "@mui/icons-material/Article";
import AccountTreeIcon         from "@mui/icons-material/AccountTree";
import AdminPanelSettingsIcon  from "@mui/icons-material/AdminPanelSettings";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import FolderIcon              from "@mui/icons-material/Folder";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Permission bit colours — owner / group / others
    pOwner:  "#DAA520",   // gold   – owner
    pGroup:  "#60a5fa",   // blue   – group
    pOthers: "#a78bfa",   // violet – others
    // Permission type colours
    pRead:    "#4ade80",  // green  – read
    pWrite:   "#fb923c",  // orange – write
    pExec:    "#f472b6",  // pink   – execute
    pNone:    "rgba(255,255,255,0.2)", // dim – no permission
    // Special bit colours
    suid:    "#ff6b35",
    sgid:    "#fbbf24",
    sticky:  "#34d399",
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ============================================================
//  SUB-COMPONENT: SectionTitle
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: "50%", background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>{label}</Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{ p: 2.5, height: "100%", background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 3, transition: "border-color 0.25s, box-shadow 0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>{title}</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>{description}</Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");
    const handleCopy = () => { navigator.clipboard.writeText(fullText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (<Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12, transition: "color 0.2s", "&:hover": { color: BRAND.gold } }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 12, md: 13 }, color: BRAND.codeText, wordBreak: "break-all" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox / SuccessBox
// ============================================================
function WarningBox({ children }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mb: 2, background: BRAND.danger, border: `1px solid ${BRAND.dangerBorder}`, borderRadius: 2 }}>
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>{children}</Typography>
        </Box>
    );
}

function SuccessBox({ children }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mb: 2, background: BRAND.success, border: `1px solid ${BRAND.successBorder}`, borderRadius: 2 }}>
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>{children}</Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5, transition: "border-color 0.25s" }}>
            <Box onClick={() => setOpen((p) => !p)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface, transition: "background 0.25s", "&:hover": { background: BRAND.goldGlow } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none", border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "border-color 0.25s, box-shadow 0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` } }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: PermissionBit
//  Renders a single permission character (r, w, x, or -) with
//  its associated colour. Used inside PermissionDiagram.
//  Props:
//    char  – "r" | "w" | "x" | "-"
//    color – colour to use when the bit is set
// ============================================================
function PermissionBit({ char }) {
    // Map each character to its semantic colour
    const colorMap = { r: BRAND.pRead, w: BRAND.pWrite, x: BRAND.pExec, "-": BRAND.pNone };
    const color = colorMap[char] || BRAND.muted;
    const active = char !== "-";

    return (
        <Box
            sx={{
                width: { xs: 28, md: 36 },
                height: { xs: 28, md: 36 },
                borderRadius: 1,
                background: active ? `${color}22` : "rgba(255,255,255,0.04)",
                border: `1px solid ${active ? color : "rgba(255,255,255,0.1)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: { xs: 14, md: 17 }, color: active ? color : BRAND.pNone, lineHeight: 1 }}>
                {char}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: PermissionDiagram
//  The main visual for the lesson — an annotated breakdown of
//  a real ls -l permission string, split into labelled sections
//  with colour-coded bits and owner/group/others badges.
// ============================================================
function PermissionDiagram() {
    // The example permission string we will dissect
    // -rw-r--r-- 1 alice developers 2048 Jan 15 report.txt
    const sections = [
        { label: "File Type",    chars: ["-"],       color: BRAND.muted,   icon: <ArticleIcon sx={{ fontSize: 14 }} />, tip: "- = regular file, d = directory, l = symlink, c = char device" },
        { label: "Owner",        chars: ["r","w","-"], color: BRAND.pOwner, icon: <PersonIcon sx={{ fontSize: 14 }} />, tip: "Owner: read + write, no execute" },
        { label: "Group",        chars: ["r","-","-"], color: BRAND.pGroup, icon: <GroupIcon sx={{ fontSize: 14 }} />, tip: "Group: read only" },
        { label: "Others",       chars: ["r","-","-"], color: BRAND.pOthers, icon: <PublicIcon sx={{ fontSize: 14 }} />, tip: "Others (everyone else): read only" },
    ];

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, p: { xs: 2, md: 3 }, mb: 3 }}>

            {/* ── Full ls -l output line ── */}
            <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 12, md: 14 }, color: BRAND.codeText, mb: 2.5, wordBreak: "break-all" }}>
                -rw-r--r-- &nbsp;1 &nbsp;<span style={{ color: BRAND.pOwner }}>alice</span> &nbsp;<span style={{ color: BRAND.pGroup }}>developers</span> &nbsp;2048 Jan 15 report.txt
            </Typography>

            {/* ── Colour-coded permission bits ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 }, mb: 2, flexWrap: "nowrap", overflowX: "auto" }}>
                {sections.map(({ chars, color }, si) => (
                    <React.Fragment key={si}>
                        {/* Coloured section bracket */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.4, md: 0.6 }, px: { xs: 0.5, md: 1 }, py: 0.8, borderRadius: 1.5, background: `${color}0a`, border: `1px solid ${color}30` }}>
                            {chars.map((ch, ci) => (
                                <PermissionBit key={ci} char={ch} />
                            ))}
                        </Box>
                        {/* Small gap between sections */}
                        {si < sections.length - 1 && <Box sx={{ width: { xs: 4, md: 8 }, flexShrink: 0 }} />}
                    </React.Fragment>
                ))}
            </Box>

            {/* ── Section labels below the bits ── */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {sections.map(({ label, color, icon, tip }) => (
                    <Tooltip key={label} title={tip} placement="top" arrow>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 20, px: 1.2, py: 0.4, cursor: "help" }}>
                            <Box sx={{ color }}>{icon}</Box>
                            <Typography sx={{ fontSize: 11, color, fontFamily: "monospace" }}>{label}</Typography>
                        </Box>
                    </Tooltip>
                ))}
            </Box>

            {/* ── Permission legend ── */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, pt: 2, borderTop: `1px solid ${BRAND.border}` }}>
                {[
                    { char: "r", label: "Read",    color: BRAND.pRead  },
                    { char: "w", label: "Write",   color: BRAND.pWrite },
                    { char: "x", label: "Execute", color: BRAND.pExec  },
                    { char: "-", label: "No permission", color: BRAND.pNone },
                ].map(({ char, label, color }) => (
                    <Box key={char} sx={{ display: "flex", alignItems: "center", gap: 0.8, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}`, borderRadius: 20, px: 1.2, py: 0.4 }}>
                        <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 13, color }}>{char}</Typography>
                        <Typography sx={{ fontSize: 11, color: BRAND.muted }}>{label}</Typography>
                    </Box>
                ))}
                {[
                    { label: "Owner",   color: BRAND.pOwner  },
                    { label: "Group",   color: BRAND.pGroup  },
                    { label: "Others",  color: BRAND.pOthers },
                ].map(({ label, color }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.7, background: `${color}12`, border: `1px solid ${color}40`, borderRadius: 20, px: 1.2, py: 0.4 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
                        <Typography sx={{ fontSize: 11, color, fontFamily: "monospace" }}>{label}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: OctalGrid
//  Visual table mapping octal digits 0–7 to their rwx meaning.
//  The most common confusion for students — this makes it visual.
// ============================================================
function OctalGrid() {
    const rows = [
        { octal: 0, binary: "000", bits: ["-","-","-"], label: "No permission"     },
        { octal: 1, binary: "001", bits: ["-","-","x"], label: "Execute only"      },
        { octal: 2, binary: "010", bits: ["-","w","-"], label: "Write only"        },
        { octal: 3, binary: "011", bits: ["-","w","x"], label: "Write + Execute"   },
        { octal: 4, binary: "100", bits: ["r","-","-"], label: "Read only"         },
        { octal: 5, binary: "101", bits: ["r","-","x"], label: "Read + Execute"    },
        { octal: 6, binary: "110", bits: ["r","w","-"], label: "Read + Write"      },
        { octal: 7, binary: "111", bits: ["r","w","x"], label: "Full permission"   },
    ];
    const colorMap = { r: BRAND.pRead, w: BRAND.pWrite, x: BRAND.pExec, "-": BRAND.pNone };

    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 3 }}>
            {/* Header */}
            <Box sx={{ display: "grid", gridTemplateColumns: "60px 70px 100px 1fr", minWidth: 340, px: 1.5, py: 1, background: BRAND.goldGlow, borderBottom: `1px solid ${BRAND.border}` }}>
                {["Octal", "Binary", "Bits", "Meaning"].map((h) => (
                    <Typography key={h} sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>{h}</Typography>
                ))}
            </Box>
            {rows.map(({ octal, binary, bits, label }, ri) => (
                <Box key={octal} sx={{ display: "grid", gridTemplateColumns: "60px 70px 100px 1fr", minWidth: 340, px: 1.5, py: 1.1, borderBottom: ri < rows.length - 1 ? `1px solid ${BRAND.border}` : "none", background: octal === 7 ? `${BRAND.gold}08` : octal === 0 ? "rgba(255,80,80,0.05)" : "transparent", "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                    {/* Octal digit */}
                    <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 16, color: BRAND.goldBright }}>{octal}</Typography>
                    {/* Binary */}
                    <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.muted }}>{binary}</Typography>
                    {/* Bit symbols */}
                    <Box sx={{ display: "flex", gap: 0.4 }}>
                        {bits.map((b, i) => (
                            <Typography key={i} sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 14, color: colorMap[b] }}>{b}</Typography>
                        ))}
                    </Box>
                    {/* Meaning */}
                    <Typography sx={{ fontSize: 12, color: BRAND.muted }}>{label}</Typography>
                </Box>
            ))}
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxPermissions
// ============================================================
export default function LinuxPermissions() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 } }}>
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Foundation · Module 03" sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 26, sm: 34, md: 42 }, mb: 2, "& span": { background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" } }}>
                        Linux <span>Permissions</span> &amp; Ownership
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 680, lineHeight: 1.8, mx: { xs: "auto", md: 0 }, mb: 3 }}>
                        Linux's permission model is the foundation of its security. Every file and directory
                        has an owner, a group, and three sets of permission bits that control exactly who
                        can read, write, or execute it. Misunderstanding permissions is the most common
                        source of "Permission denied" errors — and misusing them is the most common
                        source of security vulnerabilities.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[{ label: "~2.5 hours", sub: "read time" }, { label: "Foundation", sub: "level" }, { label: "7 sections", sub: "covered" }].map(({ label, sub }) => (
                            <Box key={label} sx={{ textAlign: "center", background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, px: 2.5, py: 1.2 }}>
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <PersonIcon />, title: "Owner", description: "Every file has one owner — the user who created it, or who was assigned ownership with chown. The owner has full control over the file's permissions and can change them freely." },
                            { icon: <GroupIcon />, title: "Group", description: "Every file belongs to one group. All members of that group share the group permissions. Groups let multiple users collaborate on files without opening them to the whole world." },
                            { icon: <PublicIcon />, title: "Others", description: "Everyone else — users who are neither the owner nor in the group — falls into 'others'. The others permission controls public access. Keep this restrictive on sensitive files." },
                            { icon: <VisibilityIcon />, title: "Read (r)", description: "For files: view content. For directories: list contents with ls. Without read on a directory, you cannot see what files are inside, even if you know their names." },
                            { icon: <EditIcon />, title: "Write (w)", description: "For files: modify, truncate, or delete content. For directories: create, rename, or delete files inside it. Write on a directory lets you delete files even if you don't own them." },
                            { icon: <PlayArrowIcon />, title: "Execute (x)", description: "For files: run as a program or script. For directories: enter with cd and access files inside. A directory with no execute bit is effectively a locked room — you can see it exists but not enter." },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – PERMISSION STRING DIAGRAM
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SearchIcon />} label="Reading Permission Strings" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        The output of <code style={{ color: BRAND.goldBright }}>ls -l</code> shows a 10-character
                        permission string. The first character is the file type — the remaining 9 are three
                        groups of three bits (owner, group, others). Hover the labels for a description.
                    </Typography>

                    {/* Animated visual diagram */}
                    <PermissionDiagram />

                    {/* File type reference */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>🟡 File Type Characters (first character)</Typography>
                    <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 3 }}>
                        {[
                            { char: "-", type: "Regular file",       example: "script.sh, data.csv, image.png"   },
                            { char: "d", type: "Directory",           example: "Documents/, /etc/, /var/log/"     },
                            { char: "l", type: "Symbolic link",       example: "/usr/bin/python → python3.11"     },
                            { char: "c", type: "Character device",    example: "/dev/tty, /dev/null, /dev/random" },
                            { char: "b", type: "Block device",        example: "/dev/sda, /dev/sdb1"              },
                            { char: "p", type: "Named pipe (FIFO)",   example: "/tmp/mypipe"                      },
                            { char: "s", type: "Socket",              example: "/run/docker.sock"                 },
                        ].map(({ char, type, example }, ri) => (
                            <Box key={char} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: 2, py: 1.2, borderBottom: ri < 6 ? `1px solid ${BRAND.border}` : "none", "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 18, color: BRAND.goldBright, minWidth: 28 }}>{char}</Typography>
                                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 600, color: BRAND.white, fontSize: 13, minWidth: 160, flexShrink: 0 }}>{type}</Typography>
                                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted, flex: 1 }}>{example}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Viewing permissions commands */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 Viewing Permissions</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ ls -l",                                           comment: "Long listing: shows permissions, owner, group, size, date" },
                        { cmd: "$ ls -la",                                          comment: "-a = include hidden files (dotfiles)" },
                        { cmd: "$ ls -lh",                                          comment: "-h = human-readable file sizes (KB, MB)" },
                        { cmd: "$ stat file.txt",                                   comment: "Detailed metadata: permissions, inode, timestamps, all in one" },
                        { cmd: "$ stat -c '%a %n' file.txt",                        comment: "Print only the octal permission and filename" },
                        { cmd: "$ find /var/www -perm /o+w",                        comment: "Find world-writable files under /var/www (security audit)" },
                        { cmd: "$ find /home -perm 777",                            comment: "Find files with exactly 777 permissions (dangerously open)" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – CHMOD
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BuildIcon />} label="chmod — Change File Permissions" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>chmod</code> changes the permission bits
                        of a file or directory. It accepts two syntaxes:{" "}
                        <strong style={{ color: BRAND.white }}>symbolic</strong> (human-readable: <code style={{ color: BRAND.goldBright }}>u+x</code>) and{" "}
                        <strong style={{ color: BRAND.white }}>octal</strong> (numeric: <code style={{ color: BRAND.goldBright }}>755</code>).
                        Both do the same thing — octal is faster to type, symbolic is easier to read.
                    </Typography>

                    {/* Octal reference grid */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>🟡 Octal Permission Reference</Typography>
                    <OctalGrid />

                    {/* Common octal combos */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>🟡 Common Octal Combinations</Typography>
                    <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 3 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "80px 120px 120px 1fr", px: 1.5, py: 1, background: BRAND.goldGlow, borderBottom: `1px solid ${BRAND.border}`, minWidth: 380 }}>
                            {["Octal", "Owner", "Group/Others", "Use Case"].map((h) => (
                                <Typography key={h} sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>{h}</Typography>
                            ))}
                        </Box>
                        {[
                            { octal: "400", o: "r--", go: "------", use: "Read-only by owner only (sensitive keys, e.g. SSH private key)" },
                            { octal: "600", o: "rw-", go: "------", use: "Read+write by owner only (config files, ~/.ssh/id_rsa)" },
                            { octal: "644", o: "rw-", go: "r--r--", use: "Owner writes, everyone reads (web files, ~/.ssh/authorized_keys)" },
                            { octal: "700", o: "rwx", go: "------", use: "Full access for owner only (private scripts, ~/.ssh/)" },
                            { octal: "755", o: "rwx", go: "r-xr-x", use: "Owner full, others read+execute (public scripts, /usr/bin programs)" },
                            { octal: "775", o: "rwx", go: "rwxr-x", use: "Owner + group full, others read+execute (team collaboration dirs)" },
                            { octal: "777", o: "rwx", go: "rwxrwx", use: "Everyone full access — DANGEROUS, avoid in production" },
                        ].map(({ octal, o, go, use }, ri) => (
                            <Box key={octal} sx={{ display: "grid", gridTemplateColumns: "80px 120px 120px 1fr", px: 1.5, py: 1.2, borderBottom: ri < 6 ? `1px solid ${BRAND.border}` : "none", minWidth: 380, background: octal === "777" ? "rgba(255,80,80,0.05)" : "transparent", "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 16, color: BRAND.goldBright }}>{octal}</Typography>
                                <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.pOwner }}>{o}</Typography>
                                <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.pGroup }}>{go}</Typography>
                                <Typography sx={{ fontSize: 12, color: octal === "777" ? BRAND.dangerText : BRAND.muted }}>{use}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Symbolic chmod */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 Symbolic chmod Syntax</Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                        Format: <code style={{ color: BRAND.goldBright }}>chmod [who][operator][permissions] file</code>.
                        Who: <code style={{ color: BRAND.pOwner }}>u</code>=user/owner,{" "}
                        <code style={{ color: BRAND.pGroup }}>g</code>=group,{" "}
                        <code style={{ color: BRAND.pOthers }}>o</code>=others,{" "}
                        <code style={{ color: BRAND.goldBright }}>a</code>=all.
                        Operator: <code style={{ color: BRAND.goldBright }}>+</code>=add,{" "}
                        <code style={{ color: BRAND.goldBright }}>-</code>=remove,{" "}
                        <code style={{ color: BRAND.goldBright }}>=</code>=set exactly.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "$ chmod u+x script.sh",                            comment: "Add execute for owner only" },
                        { cmd: "$ chmod u-x script.sh",                            comment: "Remove execute from owner" },
                        { cmd: "$ chmod go-w file.txt",                            comment: "Remove write from group and others" },
                        { cmd: "$ chmod a+r file.txt",                             comment: "Add read for everyone (owner, group, others)" },
                        { cmd: "$ chmod o= file.txt",                              comment: "Set others to no permissions (empty assignment)" },
                        { cmd: "$ chmod u=rw,g=r,o= file.txt",                    comment: "Set exact permissions: owner rw, group r, others none" },
                        { cmd: "$ chmod 755 script.sh",                            comment: "Octal: owner rwx, group r-x, others r-x" },
                        { cmd: "$ chmod 600 ~/.ssh/id_rsa",                        comment: "Secure an SSH private key (required by SSH)" },
                        { cmd: "$ chmod -R 755 /var/www/html/",                    comment: "-R = recursive: apply to all files and subdirs" },
                    ]} />

                    <WarningBox>
                        Never use <code>chmod 777</code> on files or directories in production. It grants
                        full read, write, and execute to every user on the system — including potential
                        attackers. If another user can write to your web server's files, they can serve
                        malicious content from your domain.
                    </WarningBox>

                    <ExpandableExample title="Recursive chmod with find — applying different permissions to files vs directories" icon={<FolderIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>chmod -R</code> applies the same
                            permissions to both files and directories. But files and directories often need
                            different permissions — web files should be 644, directories should be 755.
                            Use <code style={{ color: BRAND.goldBright }}>find</code> to apply them separately.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ find /var/www/html -type f -exec chmod 644 {} \\;", comment: "Set all regular files to 644 (rw-r--r--)" },
                            { cmd: "$ find /var/www/html -type d -exec chmod 755 {} \\;", comment: "Set all directories to 755 (rwxr-xr-x)" },
                            { cmd: "$ find /home/alice -type f -name '*.sh' -exec chmod +x {} \\;", comment: "Make all shell scripts executable" },
                        ]} />
                        <SuccessBox>
                            This is the standard approach for web server directories. Files readable by the web server (644), directories traversable by the web server (755), config files readable only by owner (600).
                        </SuccessBox>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – CHOWN & CHGRP
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PersonIcon />} label="chown & chgrp — Change Ownership" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>chown</code> changes who owns a file.
                        <code style={{ color: BRAND.goldBright }}>chgrp</code> changes which group a file
                        belongs to. Only root can change the owner of a file. A regular user can change
                        the group to any group they belong to.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 chown — Change Owner</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo chown alice file.txt",                      comment: "Change file owner to alice" },
                        { cmd: "$ sudo chown alice:developers file.txt",            comment: "Change owner to alice AND group to developers" },
                        { cmd: "$ sudo chown :developers file.txt",                comment: "Change group only (owner unchanged)" },
                        { cmd: "$ sudo chown -R alice:developers /var/www/myapp/", comment: "-R = recursive: change all files and subdirs" },
                        { cmd: "$ sudo chown --reference=ref.txt target.txt",      comment: "Apply same owner:group as ref.txt to target.txt" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🟡 chgrp — Change Group</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo chgrp developers file.txt",                 comment: "Change the group of file.txt to 'developers'" },
                        { cmd: "$ sudo chgrp -R www-data /var/www/html/",          comment: "Recursively set group to www-data (web server group)" },
                        { cmd: "$ chgrp $(id -gn) file.txt",                       comment: "Set group to your current primary group" },
                    ]} />

                    <ExpandableExample title="Practical web server ownership pattern" icon={<SecurityIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            A common and secure pattern for Nginx/Apache web applications: the developer owns
                            the files (can edit them), the web server group can read them, and no one else
                            can touch them.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo chown -R deploy:www-data /var/www/myapp/",  comment: "deploy user owns files, www-data group can read" },
                            { cmd: "$ sudo find /var/www/myapp -type f -exec chmod 640 {} \\;", comment: "Files: owner rw, group r, others none" },
                            { cmd: "$ sudo find /var/www/myapp -type d -exec chmod 750 {} \\;", comment: "Dirs: owner rwx, group rx, others none" },
                            { cmd: "$ sudo chmod 600 /var/www/myapp/.env",             comment: "Env file: owner only — never expose secrets to www-data" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – USERS & GROUPS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<GroupIcon />} label="Users & Groups" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Linux user and group management is tightly coupled with permissions. Every process
                        runs as a user and inherits that user's permissions. Groups are the mechanism for
                        granting shared access without making files world-readable.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🟡 User Management</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ whoami",                                          comment: "Print your current username" },
                        { cmd: "$ id",                                             comment: "Print UID, GID, and all group memberships" },
                        { cmd: "$ id alice",                                        comment: "Print UID/GID/groups for another user" },
                        { cmd: "$ sudo useradd -m -s /bin/bash alice",              comment: "-m = create home dir, -s = set default shell" },
                        { cmd: "$ sudo passwd alice",                               comment: "Set or change a user's password" },
                        { cmd: "$ sudo usermod -aG developers alice",               comment: "-aG = append alice to the developers group" },
                        { cmd: "$ sudo usermod -aG sudo alice",                     comment: "Grant alice sudo (admin) privileges" },
                        { cmd: "$ sudo userdel alice",                              comment: "Delete user alice (keeps home directory)" },
                        { cmd: "$ sudo userdel -r alice",                           comment: "-r = also delete alice's home directory" },
                        { cmd: "$ cat /etc/passwd | grep alice",                    comment: "View alice's entry: username:x:UID:GID:comment:home:shell" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🟡 Group Management</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ groups",                                          comment: "List all groups your current user belongs to" },
                        { cmd: "$ groups alice",                                    comment: "List all groups alice belongs to" },
                        { cmd: "$ sudo groupadd developers",                        comment: "Create a new group" },
                        { cmd: "$ sudo groupdel developers",                        comment: "Delete a group" },
                        { cmd: "$ cat /etc/group | grep developers",                comment: "View group entry: groupname:x:GID:members" },
                        { cmd: "$ newgrp developers",                               comment: "Switch your primary group to 'developers' for this session" },
                    ]} />

                    <ExpandableExample title="The sudo model — controlled root access" icon={<AdminPanelSettingsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>sudo</code> allows specific users to
                            run commands as root (or any other user) based on rules in{" "}
                            <code style={{ color: BRAND.goldBright }}>/etc/sudoers</code>. Always edit it
                            with <code style={{ color: BRAND.goldBright }}>visudo</code> — it validates syntax
                            before saving, preventing you from locking yourself out.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo command",                                  comment: "Run a command as root" },
                            { cmd: "$ sudo -u alice command",                         comment: "Run a command as another user (not root)" },
                            { cmd: "$ sudo -i",                                       comment: "Open an interactive root shell (use sparingly)" },
                            { cmd: "$ sudo -l",                                       comment: "List what sudo commands your user can run" },
                            { cmd: "$ sudo visudo",                                   comment: "Edit /etc/sudoers safely (validates before saving)" },
                            { cmd: "$ sudo visudo -f /etc/sudoers.d/alice",           comment: "Edit a per-user drop-in sudoers file (preferred pattern)" },
                        ]} />
                        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, p: 2, mb: 1 }}>
                            {[
                                { line: "# /etc/sudoers.d/alice",                    isComment: true },
                                { line: "# Format: user  hostname=(run-as)  commands",isComment: true },
                                { line: "",                                           },
                                { line: "# Allow alice to run any command as root:",  isComment: true },
                                { line: "alice  ALL=(ALL)  ALL",                      },
                                { line: "",                                           },
                                { line: "# Allow alice to restart nginx without a password:", isComment: true },
                                { line: "alice  ALL=(ALL)  NOPASSWD: /usr/bin/systemctl restart nginx", },
                            ].map((entry, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: entry.isComment ? "rgba(255,255,255,0.3)" : BRAND.codeText, lineHeight: 1.85 }}>
                                    {entry.line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – SPECIAL PERMISSIONS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AdminPanelSettingsIcon />} label="Special Permission Bits" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Beyond the standard rwx bits, Linux has three special permission bits that change
                        how execution and ownership work. Understanding them is essential for advanced
                        security and multi-user environments.
                    </Typography>

                    {/* SUID */}
                    <Box sx={{ mb: 3, p: 2.5, background: `${BRAND.suid}0d`, border: `1px solid ${BRAND.suid}33`, borderRadius: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.2 }}>
                            <Chip label="SUID" size="small" sx={{ background: `${BRAND.suid}22`, border: `1px solid ${BRAND.suid}55`, color: BRAND.suid, fontFamily: "monospace", fontWeight: 700, fontSize: 12 }} />
                            <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>Set User ID — runs as the file's owner, not the caller</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7, mb: 1.5 }}>
                            When set on an executable, it runs with the file owner's privileges regardless
                            of who executes it. Example: <code style={{ color: BRAND.goldBright }}>/usr/bin/passwd</code>{" "}
                            is owned by root with SUID set — this lets normal users change their own password
                            (which requires writing to <code style={{ color: BRAND.goldBright }}>/etc/shadow</code>,
                            a root-only file).
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo chmod u+s /usr/bin/myapp",                 comment: "Set SUID bit (s appears instead of x in owner position)" },
                            { cmd: "$ sudo chmod 4755 /usr/bin/myapp",                comment: "4xxx = SUID (4 + 755)" },
                            { cmd: "$ ls -la /usr/bin/passwd",                        comment: "See SUID in action: -rwsr-xr-x (s = SUID set)" },
                            { cmd: "$ find / -perm -4000 -type f 2>/dev/null",        comment: "Find all SUID files on the system (security audit)" },
                        ]} />
                    </Box>

                    {/* SGID */}
                    <Box sx={{ mb: 3, p: 2.5, background: `${BRAND.sgid}0d`, border: `1px solid ${BRAND.sgid}33`, borderRadius: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.2 }}>
                            <Chip label="SGID" size="small" sx={{ background: `${BRAND.sgid}22`, border: `1px solid ${BRAND.sgid}55`, color: BRAND.sgid, fontFamily: "monospace", fontWeight: 700, fontSize: 12 }} />
                            <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>Set Group ID — new files inherit the directory's group</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7, mb: 1.5 }}>
                            On directories: new files created inside inherit the directory's group rather than
                            the creator's primary group. Invaluable for shared project directories where all
                            team members need consistent group ownership.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo chmod g+s /shared/project/",               comment: "Set SGID on a shared directory" },
                            { cmd: "$ sudo chmod 2775 /shared/project/",              comment: "2xxx = SGID (2 + 775)" },
                            { cmd: "$ ls -ld /shared/project/",                       comment: "See SGID: drwxrwsr-x (s in group execute position)" },
                            { cmd: "$ find / -perm -2000 -type d 2>/dev/null",        comment: "Find all SGID directories" },
                        ]} />
                    </Box>

                    {/* Sticky Bit */}
                    <Box sx={{ mb: 2, p: 2.5, background: `${BRAND.sticky}0d`, border: `1px solid ${BRAND.sticky}33`, borderRadius: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.2 }}>
                            <Chip label="Sticky Bit" size="small" sx={{ background: `${BRAND.sticky}22`, border: `1px solid ${BRAND.sticky}55`, color: BRAND.sticky, fontFamily: "monospace", fontWeight: 700, fontSize: 12 }} />
                            <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>Sticky Bit — only the owner can delete their own files</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7, mb: 1.5 }}>
                            On directories: users can create and delete their own files inside, but cannot
                            delete files owned by others — even if they have write permission on the directory.
                            Classic example: <code style={{ color: BRAND.goldBright }}>/tmp</code> is
                            world-writable with the sticky bit so users can't delete each other's temp files.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ sudo chmod +t /shared/uploads/",                comment: "Set sticky bit on a shared upload directory" },
                            { cmd: "$ sudo chmod 1777 /tmp",                          comment: "1xxx = sticky bit (1 + 777) — the classic /tmp setup" },
                            { cmd: "$ ls -ld /tmp",                                   comment: "See sticky bit: drwxrwxrwt (T/t in others execute position)" },
                        ]} />
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – UMASK
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsIcon />} label="umask — Default Permissions" />
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>umask</code> defines the permissions
                        <em> removed</em> from newly created files and directories. It is subtracted from
                        the maximum defaults: 666 for files, 777 for directories. A umask of 022 results
                        in files created as 644 and directories as 755.
                    </Typography>

                    <CodeBlock lines={[
                        { cmd: "$ umask",                                           comment: "Show current umask (usually 022 or 027)" },
                        { cmd: "$ umask 022",                                       comment: "Set umask: files→644, dirs→755 (common default)" },
                        { cmd: "$ umask 027",                                       comment: "Stricter: files→640, dirs→750 (group reads, others nothing)" },
                        { cmd: "$ umask 077",                                       comment: "Maximum privacy: files→600, dirs→700 (owner only)" },
                        { cmd: "$ touch test.txt && ls -l test.txt",                comment: "Create a file and check its default permissions" },
                    ]} />

                    <ExpandableExample title="Persisting umask in shell config files" icon={<SettingsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Setting umask in the terminal only affects the current session. To make it
                            permanent, add it to the appropriate shell config file.
                        </Typography>
                        <CodeBlock lines={[
                            { cmd: "$ echo 'umask 027' >> ~/.bashrc",                 comment: "Persist for bash sessions" },
                            { cmd: "$ echo 'umask 027' >> ~/.zshrc",                  comment: "Persist for zsh sessions" },
                            { cmd: "$ echo 'umask 027' | sudo tee -a /etc/profile",   comment: "Set system-wide default for all users" },
                            { cmd: "$ source ~/.bashrc",                              comment: "Apply changes to current session immediately" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 8 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                    <Grid container spacing={1.5}>
                        {[
                            "The three permission categories: owner, group, and others",
                            "The three permission types: read, write, and execute — and what they mean on files vs directories",
                            "How to read a 10-character ls -l permission string including the file type bit",
                            "The 7 file type characters (-, d, l, c, b, p, s) and what each represents",
                            "How octal notation works: each digit maps to a binary pattern of rwx",
                            "Common octal combos: 644, 755, 700, 600 and when to use each",
                            "Both symbolic (u+x, go-w) and octal (755) chmod syntax",
                            "How to apply different permissions to files vs directories using find",
                            "How chown and chgrp change file ownership and group membership",
                            "The secure web server permission pattern: 640 files, 750 dirs",
                            "How to create, modify, and delete users and groups",
                            "The sudo model, /etc/sudoers, and the visudo command",
                            "SUID: how passwd reads /etc/shadow without being root",
                            "SGID on directories: new files inherit the directory's group",
                            "The sticky bit: why /tmp is safe even though it's world-writable",
                            "How umask subtracts from default permissions for new files",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 9 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />
                    <ResourceLink href="https://linuxize.com/post/understanding-linux-file-permissions/" label="Understanding Linux File Permissions — Linuxize" sub="Clear beginner guide to the permission model with examples and common use cases." />
                    <ResourceLink href="https://www.gnu.org/software/coreutils/manual/html_node/chmod-invocation.html" label="GNU chmod Manual" sub="Official reference for all chmod symbolic and octal modes." />
                    <ResourceLink href="https://wiki.archlinux.org/title/File_permissions_and_attributes" label="Arch Wiki — File Permissions and Attributes" sub="Comprehensive coverage including ACLs, extended attributes, and special bits." />
                    <ResourceLink href="https://man7.org/linux/man-pages/man1/chmod.1.html" label="chmod(1) — Linux Man Page" sub="Full man page including every symbolic mode operator and example." />
                    <ResourceLink href="https://man7.org/linux/man-pages/man8/visudo.8.html" label="visudo(8) — Man Page" sub="Safe sudoers editing — options, file format, and alias syntax." />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> The most
                        common permission mistake is using <code style={{ color: BRAND.goldBright }}>chmod 777</code> to "fix" a permission error.
                        Instead, ask: who needs access? Use <code style={{ color: BRAND.goldBright }}>stat file</code> to see current permissions,
                        <code style={{ color: BRAND.goldBright }}>id</code> to see your groups, and <code style={{ color: BRAND.goldBright }}>ls -la</code> to see ownership.
                        Grant the minimum permissions necessary — if the web server needs to read a file, use 644, not 777.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
